/**
 * User Service
 *
 * This service handles all user-related API calls to the Core API.
 * All functions have been migrated from Alfred service to Core API and verified to exist.
 *
 * Available Endpoints:
 * - GET /core/user/self - Get current authenticated user
 * - GET /core/user/trial-status - Get trial account status
 * - GET /core/user/organization - Get all users in current user's organization
 * - GET /core/user/roles - Get all available roles
 * - POST /core/user - Create new internal user (admin only)
 * - POST /public/user/trial-signup - Create new trial account
 *
 * @author FacilityAI Team
 * @version 2.2 (Core API Migration)
 */

import { VITE_CORE_API_URL } from "@/constants/env";
import { AuthProviders, auth } from "@/services/auth-service";
import {
	generateDeviceFingerprint,
	isDeviceFingerprintingAvailable,
} from "@/utils/device-fingerprint";

/**
 * Fetches the current authenticated user's information
 *
 * @returns {Promise<Object>} User object with organization and role data
 * @throws {Error} When user fetch fails or authentication is invalid
 *
 * @example
 * ```typescript
 * const user = await getUserSelf();
 * console.log(user.name, user.organization.name);
 * ```
 */
export const getUserSelf = async () => {
	try {
		console.log("Fetching user!");

		// Use Core API for all users (migration from Alfred to Core)
		const coreUrl = `${VITE_CORE_API_URL}/core/user/self`;
		const coreResponse = await fetch(coreUrl, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (coreResponse.status === 200) {
			const result = await coreResponse.json();
			return result.data; // Core API wraps response in { success: true, data: user }
		}

		// If both services fail, throw error
		const errorData = await coreResponse.json().catch(() => ({}));
		console.error(
			`Failed to fetch user from both services: ${errorData.message || "Unknown error"}`,
		);
		throw new Error("Failed to fetch user");
	} catch (exception: unknown) {
		console.error(`Failed to fetch user self: ${exception}`);
		throw new Error("Failed to fetch user");
	}
};

/**
 * Creates a new user account (legacy function - now redirects to trial signup)
 *
 * @param {string} name - Full name of the user
 * @param {string} email - Email address for the account
 * @param {string} password - Password for the account
 * @param {string} organization - Organization name
 * @returns {Promise<Object>} Created user object
 * @throws {Error} When signup fails or validation errors occur
 *
 * @deprecated This function now creates trial accounts. Use trialSignup directly.
 */
export const userSignup = async (
	name: string,
	email: string,
	password: string,
	organization: string,
) => {
	try {
		//First we create the authentication account in firebase
		const authResponse = await AuthProviders.createUserWithEmailAndPassword(
			auth,
			email,
			password,
		);
		const url = `${VITE_CORE_API_URL}/public/user/trial-signup`;
		const payload = {
			name: name,
			email: authResponse.user.email,
			password: password,
			organization: organization,
			external_firebase_id: authResponse.user.uid,
		};
		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		});

		// If the user triggers a 403 (forbidden) they might be snooping around, so we give less detail
		if (response.status !== 200) {
			if (response.status === 403) {
				console.error(
					"Failed to signup, please contact an Admin for assistance",
				);
				throw new Error(
					"Failed to signup, please contact an Admin for assistance",
				);
			}
			return response.json().then((errorDetails) => {
				console.error(`Failed to add user: ${errorDetails.detail}`);
				throw new Error("Failed to add user");
			});
		}

		const results = await response.json();
		return results;
	} catch (exception: unknown) {
		console.error(`Failed to signup: ${exception}`);
		throw new Error("Failed to signup");
	}
};

/**
 * Creates a new trial user account with 7-day expiration
 *
 * Includes device fingerprinting for abuse prevention and creates both
 * Firebase authentication and Core API user records.
 *
 * @param {string} name - Full name of the user
 * @param {string} email - Email address for the account
 * @param {string} password - Password for the account
 * @param {string} organization - Organization name (will be created if doesn't exist)
 * @returns {Promise<Object>} Created trial user object with organization data
 * @throws {Error} When signup fails, validation errors, or abuse prevention blocks
 *
 * @example
 * ```typescript
 * const user = await trialSignup("John Doe", "john@example.com", "password123", "Acme Corp");
 * console.log(user.isTrial, user.trialExpirationDate);
 * ```
 */
export const trialSignup = async (
	name: string,
	email: string,
	password: string,
	organization: string,
	phone?: string,
	companySize?: string,
) => {
	try {
		//First we create the authentication account in firebase
		const authResponse = await AuthProviders.createUserWithEmailAndPassword(
			auth,
			email,
			password,
		);

		const url = `${VITE_CORE_API_URL}/public/user/trial-signup`;

		// Generate device fingerprint for abuse prevention
		let deviceFingerprint: string | undefined;
		if (isDeviceFingerprintingAvailable()) {
			try {
				deviceFingerprint = generateDeviceFingerprint();
			} catch (error) {
				console.warn("Could not generate device fingerprint:", error);
			}
		}

		const payload = {
			name: name,
			email: authResponse.user.email,
			password: password,
			organization: organization,
			external_firebase_id: authResponse.user.uid,
			device_fingerprint: deviceFingerprint,
			phone_number: phone,
			company_size: companySize,
		};

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		});

		if (response.status !== 201) {
			if (response.status === 403) {
				console.error(
					"Failed to create trial account, please contact an Admin for assistance",
				);
				throw new Error(
					"Failed to create trial account, please contact an Admin for assistance",
				);
			}
			return response.json().then((errorDetails) => {
				console.error(
					`Failed to create trial account: ${errorDetails.message}`,
				);
				throw new Error(
					errorDetails.message || "Failed to create trial account",
				);
			});
		}

		const results = await response.json();

		// Sign out the user after account creation so they need to log in manually
		try {
			await AuthProviders.logout();
			console.log("Signed out user after trial account creation");
		} catch (signOutError) {
			console.error("Failed to sign out after trial creation:", signOutError);
			// Don't fail the signup if sign out fails, but log it
		}

		return results.data;
	} catch (exception: unknown) {
		console.error(`Failed to create trial account: ${exception}`);
		// Clean up Firebase user if trial creation failed
		if (auth.currentUser) {
			await auth.currentUser.delete();
		}
		throw new Error("Failed to create trial account");
	}
};

/**
 * Retrieves the trial status for the current authenticated user
 *
 * @returns {Promise<Object>} Trial status object containing:
 *   - status: 'ACTIVE' | 'EXPIRED'
 *   - daysRemaining: number of days left in trial
 *   - endDate: ISO string of trial expiration date
 * @throws {Error} When user is not a trial account or fetch fails
 *
 * @example
 * ```typescript
 * const status = await getTrialStatus();
 * if (status.daysRemaining < 3) {
 *   showTrialExpirationWarning();
 * }
 * ```
 */
export const getTrialStatus = async () => {
	try {
		const idToken = await auth.currentUser?.getIdToken();
		if (!idToken) {
			throw new Error("No authentication token");
		}

		const url = `${VITE_CORE_API_URL}/core/user/trial-status`;
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${idToken}`,
			},
		});

		if (response.status !== 200) {
			throw new Error("Failed to get trial status");
		}

		const results = await response.json();
		return results.data;
	} catch (exception: unknown) {
		console.error(`Failed to get trial status: ${exception}`);
		throw new Error("Failed to get trial status");
	}
};

////////////////////////////////////////////////////////////
// Stub Functions - Core API Endpoints Not Implemented
////////////////////////////////////////////////////////////

/**
 * Fetches all users from the current user's organization
 *
 * @returns {Promise<Array>} Array of user objects with role and organization data
 * @throws {Error} When API call fails or user lacks admin permissions
 *
 * @example
 * ```typescript
 * const users = await getOrganizationUsers();
 * console.log(`Found ${users.length} users in organization`);
 * ```
 */
export const getOrganizationUsers = async (): Promise<any[]> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/user/organization`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(
				errorData.message || "Failed to fetch organization users",
			);
		}

		const result = await response.json();
		return result.data; // Core API wraps response in { success: true, data: users }
	} catch (exception: any) {
		console.error("Failed to fetch organization users:", exception);
		throw new Error(exception.message || "Failed to fetch organization users");
	}
};

/**
 * Fetches all available roles from the Core API
 *
 * Returns roles based on the current user's access level:
 * - Super admins see all roles
 * - Admins see all roles except SUPERUSER
 * - Regular users see only USER role
 *
 * @returns {Promise<Array>} Array of role objects with id, name, and accessLevel
 * @throws {Error} When API call fails
 *
 * @example
 * ```typescript
 * const roles = await getRoles();
 * // [{ id: 1, name: "USER", accessLevel: 100 }, { id: 2, name: "ADMIN", accessLevel: 500 }]
 * ```
 */
export const getRoles = async (): Promise<any[]> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/user/roles`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || "Failed to fetch roles");
		}

		const result = await response.json();
		return result.data; // Core API wraps response in { success: true, data: roles }
	} catch (exception: any) {
		console.error("Failed to fetch roles:", exception);
		throw new Error(exception.message || "Failed to fetch roles");
	}
};

/**
 * Fetches users who submitted to a specific form (STUB FUNCTION)
 * @deprecated Core API does not currently support this endpoint
 */
export const getOrganizationSubmittedUsers = async (
	formId: string | number,
): Promise<any[]> => {
	console.warn(
		`getOrganizationSubmittedUsers(${formId}): Core API endpoint not implemented, returning empty array`,
	);
	return [];
};

/**
 * Adds a new user internally to the current organization
 *
 * Creates both Firebase authentication and Core API database records.
 * The Core API handles Firebase user creation using Admin SDK, so the admin's
 * session is not affected.
 *
 * Requires admin permissions (access_level >= 500).
 *
 * @param {string} name - Full name of the user
 * @param {string} email - Email address for the account
 * @param {string} password - Password for the account (min 6 characters)
 * @param {string | number} role_id - Role ID to assign (1=USER, 2=VERIZON, 3=ADMIN, 4=SUPERUSER)
 * @returns {Promise<Object>} Created user object
 * @throws {Error} When user creation fails or validation errors occur
 *
 * @example
 * ```typescript
 * await addUserInternal("John Doe", "john@example.com", "password123", 1);
 * ```
 */
export const addUserInternal = async (
	name: string,
	email: string,
	password: string,
	role_id: string | number,
): Promise<any> => {
	try {
		const adminToken = await auth?.currentUser?.getIdToken();

		if (!adminToken) {
			throw new Error("Not authenticated. Please log in again.");
		}

		// Send request to Core API - it will handle Firebase user creation using Admin SDK
		const url = `${VITE_CORE_API_URL}/core/user`;
		const payload = {
			name: name,
			email: email,
			password: password,
			role_id: role_id,
		};

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${adminToken}`,
			},
			body: JSON.stringify(payload),
		});

		if (response.status !== 201) {
			if (response.status === 403) {
				throw new Error("Insufficient permissions. Admin access required.");
			}

			const errorDetails = await response
				.json()
				.catch(() => ({ message: "Unknown error" }));
			console.error(`Failed to add user: ${errorDetails.message}`);
			throw new Error(errorDetails.message || "Failed to add user");
		}

		const results = await response.json();
		return results.data;
	} catch (exception: unknown) {
		console.error(`Failed to add user internally: ${exception}`);
		if (exception instanceof Error) {
			throw exception;
		}
		throw new Error("Failed to add user");
	}
};

/**
 * Updates a user by admin (STUB FUNCTION)
 * @deprecated Core API does not currently support this endpoint
 */
export const adminUpdateUser = async (
	userId: string | number,
	body: {
		name: string;
		role_id: string | number;
		organization_id: string | number;
	},
): Promise<{ success: boolean }> => {
	console.warn(
		`adminUpdateUser(${userId}): Core API endpoint not implemented, no-op`,
	);
	return { success: true };
};

/**
 * Updates current user's basic info (STUB FUNCTION)
 * @deprecated Core API does not currently support this endpoint
 */
export const selfUpdateUser = async (body: {
	saved_filters?: { [x: string]: unknown };
	profile_picture_url?: string;
}): Promise<{ success: boolean }> => {
	console.warn("selfUpdateUser: Core API endpoint not implemented, no-op");
	return { success: true };
};

/**
 * Updates user's current organization (implements organization switching)
 *
 * @param {Object} user - User object with updated organization_id
 * @returns {Promise<Object>} Updated user object from Core API
 * @throws {Error} When update fails or user lacks access to organization
 *
 * @example
 * ```typescript
 * const updatedUser = await selfUpdateUserComprehensive({ organization_id: 123 });
 * console.log(updatedUser.organization.name);
 * ```
 */
export const selfUpdateUserComprehensive = async (user: any): Promise<any> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/user/self/organization`;

		const response = await fetch(url, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify({
				organization_id: user.organization_id,
			}),
		});

		if (response.status !== 200) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || "Failed to update organization");
		}

		const result = await response.json();
		return result.data; // Return updated user object
	} catch (exception: any) {
		console.error("Failed to update user organization:", exception);
		throw new Error(exception.message || "Failed to update organization");
	}
};

/**
 * Deletes a user from the organization
 * Requires admin privileges
 *
 * @param {string | number} userId - ID of the user to delete
 * @returns {Promise<{ success: boolean }>}
 * @throws {Error} When deletion fails or user lacks permissions
 */
export const deleteUser = async (
	userId: string | number,
): Promise<{ success: boolean }> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/user/${userId}`;
		const response = await fetch(url, {
			method: "DELETE",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		const data = await response.json();

		if (!response.ok || !data.success) {
			throw new Error(data.message || "Failed to delete user");
		}

		return { success: true };
	} catch (error: any) {
		console.error("Error deleting user:", error);
		throw new Error(error.message || "Failed to delete user");
	}
};

/**
 * Updates a user's details (admin only)
 *
 * @param {string | number} userId - ID of the user to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<{ success: boolean }>}
 * @throws {Error} When update fails
 */
export const updateUser = async (
	userId: string | number,
	updates: { name?: string; role_id?: number; phone_number?: string },
): Promise<{ success: boolean }> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/user/${userId}`;
		const response = await fetch(url, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify(updates),
		});

		const data = await response.json();
		if (!response.ok || !data.success) {
			throw new Error(data.message || "Failed to update user");
		}

		return { success: true };
	} catch (error: any) {
		console.error("Error updating user:", error);
		throw new Error(error.message || "Failed to update user");
	}
};

/**
 * Generates a password reset link for a user (admin only)
 *
 * @param {string | number} userId - ID of the user to reset password for
 * @returns {Promise<{ resetLink: string; email: string }>}
 * @throws {Error} When password reset fails
 */
export const resetUserPassword = async (
	userId: string | number,
): Promise<{ resetLink: string; email: string }> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/user/${userId}/reset-password`;
		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		const data = await response.json();
		if (!response.ok || !data.success) {
			throw new Error(data.message || "Failed to generate password reset");
		}

		return data.data;
	} catch (error: any) {
		console.error("Error resetting user password:", error);
		throw new Error(error.message || "Failed to generate password reset");
	}
};

/**
 * Toggles a user's disabled status (admin only)
 *
 * @param {string | number} userId - ID of the user to toggle
 * @param {boolean} disabled - Whether the account should be disabled
 * @returns {Promise<{ success: boolean; disabled: boolean }>}
 * @throws {Error} When toggle fails
 */
export const toggleUserDisabled = async (
	userId: string | number,
	disabled: boolean,
): Promise<{ success: boolean; disabled: boolean }> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/user/${userId}/toggle-disable`;
		const response = await fetch(url, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify({ disabled }),
		});

		const data = await response.json();
		if (!response.ok || !data.success) {
			throw new Error(data.message || "Failed to update user status");
		}

		return { success: true, disabled: data.data.disabled };
	} catch (error: any) {
		console.error("Error toggling user disabled:", error);
		throw new Error(error.message || "Failed to update user status");
	}
};

/**
 * Fetches user notifications (STUB FUNCTION)
 * @deprecated Core API does not currently support notifications
 */
export const getNotifications = async (): Promise<any[]> => {
	console.warn(
		"getNotifications: Core API endpoint not implemented, returning empty array",
	);
	return [];
};

/**
 * Marks all notifications as read (STUB FUNCTION)
 * @deprecated Core API does not currently support notifications
 */
export const setNotificationsToRead = async (): Promise<{
	success: boolean;
}> => {
	console.warn(
		"setNotificationsToRead: Core API endpoint not implemented, no-op",
	);
	return { success: true };
};

/**
 * Deletes a notification (STUB FUNCTION)
 * @deprecated Core API does not currently support notifications
 */
export const deleteNotification = async (
	notificationId: string | number,
): Promise<{ success: boolean }> => {
	console.warn(
		`deleteNotification(${notificationId}): Core API endpoint not implemented, no-op`,
	);
	return { success: true };
};

/**
 * Creates a user invitation for the current organization
 *
 * Generates an invitation token that can be shared with the new user.
 * The user will use this token to complete their registration via AcceptInvitation page.
 *
 * @param {string} email - Email address to invite
 * @param {number} role_id - Role ID to assign to the invited user
 * @returns {Promise<Object>} Invitation object with token
 * @throws {Error} When invitation creation fails
 */
export const createInvitation = async (
	email: string,
	role_id: number,
): Promise<{ token: string; email: string }> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/user/invite`;

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify({ email, role_id }),
		});

		if (response.status !== 201 && response.status !== 200) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || "Failed to create invitation");
		}

		const result = await response.json();
		return result.data;
	} catch (exception: any) {
		console.error("Failed to create invitation:", exception);
		throw new Error(exception.message || "Failed to create invitation");
	}
};

/**
 * Retrieves invitation details by token (public, no auth required)
 *
 * @param {string} token - Invitation token from the invite link
 * @returns {Promise<Object>} Invitation details with email, organization, and role
 * @throws {Error} When invitation is not found, expired, or already used
 */
export const getInvitationByToken = async (token: string): Promise<any> => {
	try {
		const url = `${VITE_CORE_API_URL}/public/invitation/${token}`;

		const response = await fetch(url, { method: "GET" });

		if (response.status === 404) {
			throw new Error("This invitation is no longer valid. It may have expired or already been used.");
		}

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || "Failed to load invitation");
		}

		const result = await response.json();
		return result.data;
	} catch (exception: any) {
		console.error("Failed to get invitation:", exception);
		throw new Error(exception.message || "Failed to load invitation");
	}
};

/**
 * Accepts an invitation, creating a new user account (public, no auth required)
 *
 * @param {string} token - Invitation token from the invite link
 * @param {string} name - Full name for the new user
 * @param {string} password - Password for the new account
 * @returns {Promise<Object>} Result with the created user's email
 * @throws {Error} When account creation fails
 */
export const acceptInvitation = async (
	token: string,
	name: string,
	password: string,
): Promise<{ email: string }> => {
	try {
		const url = `${VITE_CORE_API_URL}/public/invitation/${token}/accept`;

		const response = await fetch(url, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ name, password }),
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || "Failed to create account");
		}

		const result = await response.json();
		return result.data;
	} catch (exception: any) {
		console.error("Failed to accept invitation:", exception);
		throw new Error(exception.message || "Failed to create account");
	}
};
